import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  HStack,
  Spinner,
  Text,
  VStack,
  useToast,
  Link,
  Progress,
} from '@chakra-ui/react';
import {
  disableWarmup,
  enableWarmup,
  fetchWarmupEntitlements,
  getWarmupStatus,
  listWarmupAccounts,
  startEmailWarmingCheckout,
} from '../../services/warmupService';
import { applySeatCheckoutResponse } from '../../utils/stripeSeatCheckout';
import { formatUsdFromCents } from '../../utils/formatCurrency';
import {
  bodyTextProps,
  linkProps,
  listContainerProps,
  listRowProps,
  mutedTextProps,
  outlineButtonProps,
  primaryButtonProps,
  rowTitleTextProps,
  sectionTitleProps,
} from '../../pages/emailInfraPageTheme';

const WarmingProgressBar = ({ st, acc }) => {
  const show = st.warmup_show_progress ?? acc.warmup_show_progress ?? false;
  if (!show) return null;

  const percent = Math.min(
    100,
    Math.max(0, st.warmup_progress_percent ?? acc.warmup_progress_percent ?? 0)
  );
  const label =
    st.warmup_progress_label ?? acc.warmup_progress_label ?? 'Warming in progress';
  const fully = st.warmup_fully_warmed ?? acc.warmup_fully_warmed;
  const daysTotal = st.warmup_days_total ?? acc.warmup_days_total ?? 14;

  return (
    <Box mt={3} maxW={{ base: '100%', sm: '340px' }}>
      <HStack justify="space-between" mb={1.5}>
        <Text fontSize="xs" color="rgba(255,255,255,0.45)">
          {label}
        </Text>
        <Text fontSize="xs" color="rgba(255,255,255,0.35)">
          {percent}%
          {!fully && (
            <Text as="span" display={{ base: 'none', sm: 'inline' }}>
              {' '}
              · ~{daysTotal} days to full warm
            </Text>
          )}
        </Text>
      </HStack>
      <Progress
        value={percent}
        size="sm"
        borderRadius="full"
        bg="rgba(255,255,255,0.08)"
        colorScheme={fully ? 'green' : 'gray'}
        sx={{
          '& > div': {
            bg: fully ? '#4ade80' : 'rgba(255,255,255,0.55)',
            transition: 'width 0.3s ease',
          },
        }}
      />
    </Box>
  );
};

/**
 * Email warming controls embedded on Domains & inboxes (step 4 of the flow).
 */
const EmailWarmingSection = ({ parentPaidOk }) => {
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [entitlements, setEntitlements] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [statusByKey, setStatusByKey] = useState({});
  const [busyKey, setBusyKey] = useState(null);
  const [checkoutBusyKey, setCheckoutBusyKey] = useState(null);

  const paidOk = parentPaidOk ?? entitlements?.can_use_email_infra;
  const byoSeatsRemaining = entitlements?.byo_warming_seats_remaining ?? 0;
  const warmingMo = formatUsdFromCents(entitlements?.email_warming_monthly_gr_cents ?? 2850);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const ent = await fetchWarmupEntitlements();
      setEntitlements(ent);
      if (!ent.can_use_email_infra) {
        setAccounts([]);
        return;
      }
      const { accounts: rows } = await listWarmupAccounts();
      setAccounts(rows || []);
      const statuses = {};
      await Promise.all(
        (rows || []).map(async (acc) => {
          if (!acc.account_email) return;
          try {
            const st = await getWarmupStatus(acc.provider, acc.account_email);
            statuses[`${acc.provider}:${acc.account_email}`] = st;
          } catch {
            /* ignore per-account */
          }
        })
      );
      setStatusByKey(statuses);
    } catch (e) {
      toast({
        title: 'Could not load warming',
        description: e.response?.data?.detail || e.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const warmingCheckout = searchParams.get('warming_checkout');
    if (!warmingCheckout) return;
    setSearchParams({}, { replace: true });
    if (warmingCheckout === 'success') {
      toast({
        title: 'Warming subscription updated',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      load();
    }
  }, [searchParams, setSearchParams, toast, load]);

  const buyWarming = async (acc) => {
    const key = `${acc.provider}:${acc.account_email}`;
    setCheckoutBusyKey(key);
    try {
      const data = await startEmailWarmingCheckout(1);
      await applySeatCheckoutResponse(data, {
        toast,
        onUpdated: load,
        redirect: true,
      });
    } catch (e) {
      toast({
        title: 'Checkout failed',
        description: e.response?.data?.detail || e.message,
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setCheckoutBusyKey(null);
    }
  };

  const toggleWarmup = async (acc, enable) => {
    const key = `${acc.provider}:${acc.account_email}`;
    setBusyKey(key);
    try {
      if (enable) {
        await enableWarmup(acc.provider, acc.account_email);
        toast({ title: 'Warming enabled', status: 'success', duration: 4000, isClosable: true });
      } else {
        await disableWarmup(acc.provider, acc.account_email);
        toast({ title: 'Warming paused', status: 'info', duration: 4000, isClosable: true });
      }
      await load();
    } catch (e) {
      toast({
        title: 'Action failed',
        description: e.response?.data?.detail || e.message,
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setBusyKey(null);
    }
  };

  if (!paidOk) {
    return null;
  }

  return (
    <Box id="email-warming" scrollMarginTop="6rem">
      <Text {...sectionTitleProps} mb={2}>
        Email warming
      </Text>
      <Text {...bodyTextProps} mb={4}>
        After OAuth, inboxes ramp to fully warmed over about{' '}
        {entitlements?.warmup_full_warm_days ?? 14} days. Purchased inboxes include warming; BYO
        mailboxes from Integrations need a warming seat.
      </Text>

      {loading && accounts.length === 0 ? (
        <HStack py={6} justify="center">
          <Spinner size="md" color="blue.400" />
        </HStack>
      ) : accounts.length === 0 ? (
        <Text {...mutedTextProps}>
          No connected mailboxes yet. Finish provisioning and Connect OAuth above, or link an
          existing mailbox in{' '}
          <Link as={RouterLink} to="/integrations" {...linkProps}>
            Integrations
          </Link>
          .
        </Text>
      ) : (
        <Box {...listContainerProps}>
          {accounts.map((acc, i) => {
            const key = `${acc.provider}:${acc.account_email}`;
            const st = statusByKey[key] || {};
            const isPurchasedInbox = acc.mailbox_source === 'zapmail';
            const enabled = acc.warmup_enabled || st.warmup_enabled;
            const providerLabel = acc.provider === 'outlook' ? 'Outlook' : 'Gmail';
            const canEnableByo = !isPurchasedInbox && (enabled || byoSeatsRemaining > 0);
            const needsWarmingPurchase = !isPurchasedInbox && !enabled && byoSeatsRemaining <= 0;
            const isLast = i === accounts.length - 1;
            const warmupStatus = (st.warmup_status || '').toLowerCase();
            const showRunning =
              enabled || warmupStatus === 'running' || warmupStatus === 'started';

            return (
              <HStack
                key={key}
                justify="space-between"
                align="flex-start"
                flexWrap="wrap"
                gap={3}
                {...listRowProps(isLast)}
              >
                <Box flex={1} minW="220px">
                  <Text {...rowTitleTextProps}>{acc.account_email}</Text>
                  <HStack spacing={2} mt={1.5} flexWrap="wrap">
                    <Text fontSize="xs" color="rgba(255,255,255,0.35)" letterSpacing="0.04em">
                      {providerLabel.toUpperCase()}
                    </Text>
                    {isPurchasedInbox ? (
                      <Box
                        px={1.5}
                        py="1px"
                        borderRadius="full"
                        bg="rgba(168,85,247,0.12)"
                        border="1px solid rgba(168,85,247,0.35)"
                      >
                        <Text
                          fontSize="9px"
                          color="#c084fc"
                          fontWeight="600"
                          letterSpacing="0.06em"
                          textTransform="uppercase"
                        >
                          Purchased · warming included
                        </Text>
                      </Box>
                    ) : (
                      <Box
                        px={1.5}
                        py="1px"
                        borderRadius="full"
                        border="1px solid rgba(255,255,255,0.12)"
                        bg="rgba(255,255,255,0.05)"
                      >
                        <Text
                          fontSize="9px"
                          color="rgba(255,255,255,0.45)"
                          fontWeight="600"
                          letterSpacing="0.06em"
                          textTransform="uppercase"
                        >
                          Your domain
                        </Text>
                      </Box>
                    )}
                    {showRunning && (
                      <Box
                        px={1.5}
                        py="1px"
                        borderRadius="full"
                        bg="rgba(34,197,94,0.1)"
                        border="1px solid rgba(34,197,94,0.3)"
                      >
                        <Text
                          fontSize="9px"
                          color="#4ade80"
                          fontWeight="600"
                          letterSpacing="0.06em"
                          textTransform="uppercase"
                        >
                          Warming on
                        </Text>
                      </Box>
                    )}
                    {st.warmup_status && !showRunning && (
                      <Text fontSize="xs" color="rgba(255,255,255,0.28)">
                        {st.warmup_status}
                      </Text>
                    )}
                  </HStack>
                  <WarmingProgressBar st={st} acc={acc} />
                  {!isPurchasedInbox && needsWarmingPurchase && (
                    <Text fontSize="xs" color="rgba(255,255,255,0.28)" mt={2}>
                      {warmingMo}/mo for this inbox
                    </Text>
                  )}
                </Box>
                <HStack flexShrink={0} spacing={3} align="center">
                  {needsWarmingPurchase && (
                    <>
                      <Box textAlign="right" display={{ base: 'none', sm: 'block' }}>
                        <Text fontSize="xs" color="rgba(255,255,255,0.45)" letterSpacing="0.02em">
                          {warmingMo}/mo
                        </Text>
                      </Box>
                      <Button
                        {...primaryButtonProps}
                        isLoading={checkoutBusyKey === key}
                        onClick={() => buyWarming(acc)}
                      >
                        Buy warming
                      </Button>
                    </>
                  )}
                  {(isPurchasedInbox || canEnableByo) && !enabled && !needsWarmingPurchase && (
                    <Button
                      {...primaryButtonProps}
                      isLoading={busyKey === key}
                      onClick={() => toggleWarmup(acc, true)}
                    >
                      Start warming
                    </Button>
                  )}
                  {enabled && (
                    <Button
                      {...outlineButtonProps}
                      isLoading={busyKey === key}
                      onClick={() => toggleWarmup(acc, false)}
                    >
                      Pause
                    </Button>
                  )}
                </HStack>
              </HStack>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default EmailWarmingSection;
